import React, { useState } from "react";
import PropTypes from "prop-types";

import { REFERRAL_FORM, WITHIN_OPTIONS, BY_PREFIX, isByDate, withinPhrase } from "../helper/rxData";
import SpecialtyPicker from "./SpecialtyPicker";
import { shortDate } from "../helper/rxMappers";

/**
 * Card · Referrals (Figma 2398:3210) — the referrals added in this consultation,
 * over the "NEW REFERRAL" form.
 *
 * The form is CLOSED until "Add new" is clicked. It used to be open on load, which
 * put an empty form above the list on every visit and made the card look like it
 * always wanted input.
 *
 * Added referrals are the parent's list (they print under "Referral" in the
 * document), so add/tick go out through `onAdd`/`onToggle`. The half-typed form is
 * local: nothing reaches the document until "Add referral".
 *
 * Unticking a row DESELECTS it — it drops out of the document and the payload but
 * stays on the card, so it can be ticked again. Nothing here deletes a referral: a
 * mis-click used to destroy typed clinical text with no way back.
 */
const ReferralsCard = ({ referrals, onAdd, onToggle }) => {
  const [form, setForm] = useState(REFERRAL_FORM);
  const [formOpen, setFormOpen] = useState(false);
  /* "Pick date" reveals a date field instead of being a fourth window. It used to
     be an ordinary pill, so clicking it set the literal string "Pick date" and the
     prescription printed "within Pick date of this prescription" — the CTA did not
     just fail silently, it put nonsense on a signed document. */
  const [picking, setPicking] = useState(false);
  const [pickedOn, setPickedOn] = useState("");

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // A referral with no specialty would print an empty bullet on the document.
  const incomplete = form.specialty.trim() === "";

  /* Back to an empty form: after a referral is added, and on Cancel. The form
     lives here, not in the part of the card that unmounts when it closes, so
     Cancel used to only HIDE it — "Add new" then brought back the abandoned
     specialty, reason and window. */
  const reset = () => {
    setForm(REFERRAL_FORM);
    setPicking(false);
    setPickedOn("");
  };

  const add = () => {
    onAdd({ ...form });
    reset();
  };

  const cancel = () => {
    reset();
    setFormOpen(false);
  };

  return (
    <section className="rx-card">
      <header className="rx-card-head">
        <h3 className="rx-card-title">Referrals</h3>
        <span className="rx-card-meta">appears under “Referral” in the prescription</span>
        <div className="rx-card-head-end">
          <button type="button" className="rx-linkbtn" onClick={() => setFormOpen(true)}>
            Add new
          </button>
        </div>
      </header>

      <div className="rx-group">
        <p className="rx-group-cap">
          Added in this consultation<span className="rx-group-dot">·</span>
          <span className="rx-group-n">{referrals.length}</span>
        </p>

        <ul className="rx-rows">
          {referrals.map((r) => (
            <li className={`rx-ref-row${r.selected === false ? " is-off" : ""}`} key={r.id}>
              <label className="rx-row-main">
                {/* `selected !== false` so a referral restored from a draft written
                    before this flag existed still shows as ticked. */}
                <input
                  type="checkbox"
                  className="rx-cb-input"
                  checked={r.selected !== false}
                  onChange={() => onToggle(r.id)}
                />
                <span className="rx-cb" aria-hidden="true">
                  <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    aria-hidden="true"
                  >
                    <path d="M3.5 8.4l3 3 6-6.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="rx-row-body">
                  <span className="rx-ref-spec">{r.specialty}</span>
                  <span className="rx-ref-sub">
                    {/* "Within 1 week" vs "by 5 Sep, 2026" — one phrase builder, so the
                        row, the document and the PDF cannot word it differently. */}
                    {withinPhrase(r.within).replace(" of this prescription", "")}
                    {r.reason ? ` · Reason: ${r.reason}` : ""}
                  </span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </div>

      {formOpen ? (
        <div className="rx-ref-form">
          <p className="rx-group-cap">New referral</p>

          {/* Full width, not half: "Preferred doctor / centre" used to sit beside it
              in an rx-grid-2. That field was removed — it was typed into but read by
              nothing (not the document, not the payload), so it only ever collected
              text that was silently discarded. */}
          <div className="rx-field">
            <label className="rx-field-label" htmlFor="rx-ref-spec">
              Specialty
            </label>
            {/* A searchable dropdown over the service names of the four
                referable consultation subtypes (product, 2026-09-10). Typing
                SEARCHES it; the specialty can only be one of the rows
                (product, 2026-09-12 — custom specialties were reaching signed
                prescriptions). `form.specialty` is therefore either empty or a
                catalogue label, which is what `incomplete` below relies on. */}
            <span className="rx-field-wrap">
              <SpecialtyPicker
                id="rx-ref-spec"
                value={form.specialty}
                onChange={(v) => set("specialty", v)}
                placeholder="Search and select a specialty"
              />
            </span>
          </div>

          <div className="rx-field">
            <label className="rx-field-label" htmlFor="rx-ref-reason">
              Reason for referral · goes on the prescription
            </label>
            <span className="rx-field-wrap">
              <input
                id="rx-ref-reason"
                type="text"
                className="rx-input"
                value={form.reason}
                onChange={(e) => set("reason", e.target.value)}
              />
            </span>
          </div>

          <div className="rx-field">
            <span className="rx-field-label">To be done within</span>
            <div className="rx-pills">
              {WITHIN_OPTIONS.map((o) => (
                <button
                  key={o}
                  type="button"
                  className={`rx-pill${!picking && form.within === o ? " is-on" : ""}`}
                  aria-pressed={!picking && form.within === o}
                  onClick={() => {
                    setPicking(false);
                    setPickedOn("");
                    set("within", o);
                  }}
                >
                  {o}
                </button>
              ))}
              <button
                type="button"
                className={`rx-pill${picking || isByDate(form.within) ? " is-on" : ""}`}
                aria-pressed={picking || isByDate(form.within)}
                aria-expanded={picking}
                onClick={() => {
                  // Opening it does NOT change `within` — the referral keeps the
                  // window it had until a date is actually chosen, so an accidental
                  // click cannot leave the field in a half-set state.
                  setPicking((v) => !v);
                }}
              >
                Pick date
              </button>
            </div>

            {picking && (
              <span className="rx-field-wrap rx-ref-date">
                <input
                  type="date"
                  className="rx-input"
                  aria-label="Referral due date"
                  value={pickedOn}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPickedOn(v);
                    // Stored as a finished phrase ("by 5 Sep, 2026"): a deadline and a
                    // window need different sentences around them, and withinPhrase is
                    // the single place that difference lives.
                    if (v) set("within", `${BY_PREFIX}${shortDate(v)}`);
                  }}
                />
              </span>
            )}
          </div>

          <div className="rx-ref-actions">
            <button type="button" className="rx-btn is-white" onClick={cancel}>
              Cancel
            </button>
            <button type="button" className="rx-btn is-primary" disabled={incomplete} onClick={add}>
              Add referral
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
};
ReferralsCard.propTypes = {
  referrals: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      specialty: PropTypes.string.isRequired,
      within: PropTypes.string.isRequired,
      reason: PropTypes.string.isRequired,
      // Absent on referrals restored from a draft written before the flag existed.
      selected: PropTypes.bool,
    })
  ).isRequired,
  onAdd: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default ReferralsCard;
